import { Card, Row, Col, Statistic, theme, Typography } from "antd";

import {
  getChangeDirection,
  shuffledColors,
  stripMarkdown,
} from "../cardUtils";
import { MetricDisplay } from "./MetricDisplay";

export interface KeyMetric {
  metric: string;
  current_value: number;
  previous_value?: number;
  percent_change?: string;
  unit?: string;
}

interface KeyMetricsGridProps {
  metrics: KeyMetric[];
  maxItems?: number;
  variant?:
    | "default"
    | "modern"
    | "simpleCard"
    | "softCard"
    | "simpleRow"
    | "softRow"
    | "horizontal-compact";
  renderMetric?: (metric: KeyMetric) => React.ReactNode;
}

const CardVariant = ({
  metrics = [],
  maxItems,
  renderMetric,
}: KeyMetricsGridProps) => {
  if (!metrics || metrics.length === 0) {
    return null;
  }
  const displayMetrics = maxItems ? metrics.slice(0, maxItems) : metrics;
  const colors = shuffledColors(metrics[0].metric);
  return (
    <div className="responsive-card-grid-small">
      {displayMetrics.map((stat, index) =>
        renderMetric ? (
          renderMetric(stat)
        ) : (
          <MetricDisplay
            key={`card-${index}-${stat.metric}`}
            variant="card"
            label={stripMarkdown(stat.metric)}
            value={Number(stat.current_value).toFixed(2)}
            unit={stat.unit}
            percent={stat.percent_change}
            changeType={getChangeDirection(stat.percent_change)}
            color={colors[index]}
            style={{ padding: 0 }}
          />
        )
      )}
    </div>
  );
};

const ModernVariant = ({
  metrics = [],
  maxItems,
  renderMetric,
}: KeyMetricsGridProps) => {
  const { token } = theme.useToken();
  const displayMetrics = maxItems ? metrics.slice(0, maxItems) : metrics;

  return (
    <div className="responsive-card-grid-small">
      {displayMetrics.map((stat, index) =>
        renderMetric ? (
          renderMetric(stat)
        ) : (
          <MetricDisplay
            key={`modern-${index}-${stat.metric}`}
            variant="modern"
            label={stripMarkdown(stat.metric)}
            value={Number(stat.current_value).toFixed(2)}
            unit={stat.unit}
            percent={stat.percent_change}
            changeType={getChangeDirection(stat.percent_change)}
            style={{ width: "100%" }}
          />
        )
      )}
    </div>
  );
};

const InlineVariant = ({
  metrics = [],
  maxItems,
  renderMetric,
}: KeyMetricsGridProps) => {
  const { token } = theme.useToken();
  const displayMetrics = maxItems ? metrics.slice(0, maxItems) : metrics;
  const colors = shuffledColors(metrics[0].metric);
  return (
    <div className="responsive-card-grid-small">
      {displayMetrics.map((stat, index) =>
        renderMetric ? (
          renderMetric(stat)
        ) : (
          <MetricDisplay
            key={`inline-${index}-${stat.metric}`}
            variant="default"
            label={stripMarkdown(stat.metric)}
            value={Number(stat.current_value).toFixed(2)}
            unit={stat.unit}
            percent={stat.percent_change}
            changeType={getChangeDirection(stat.percent_change)}
            className="vzbl-indent-medium"
            style={{
              background: colors[index],
              borderRadius: "var(--border-radius-2)",
            }}
          />
        )
      )}
    </div>
  );
};

// Soft Card Variant: Each metric in a card with a soft background
const SoftCardVariant = ({
  metrics = [],
  maxItems,
  renderMetric,
}: KeyMetricsGridProps) => {
  const displayMetrics = maxItems ? metrics.slice(0, maxItems) : metrics;
  return (
    <div className="responsive-card-grid-small">
      {displayMetrics.map((stat, index) =>
        renderMetric ? (
          renderMetric(stat)
        ) : (
          <MetricDisplay
            key={`soft-card-${index}-${stat.metric}`}
            variant="card"
            label={stripMarkdown(stat.metric)}
            value={Number(stat.current_value).toFixed(2)}
            unit={stat.unit}
            percent={stat.percent_change}
            changeType={getChangeDirection(stat.percent_change)}
            style={{
              background: "var(--color-background-tertiary)",
              borderRadius: "var(--border-radius-2)",
              width: "100%",
              padding: 0,
            }}
          />
        )
      )}
    </div>
  );
};

// Simple Row Variant: Metrics in a row, no card, divider below
const SimpleRowVariant = ({
  metrics = [],
  maxItems,
  renderMetric,
}: KeyMetricsGridProps) => {
  const displayMetrics = maxItems ? metrics.slice(0, maxItems) : metrics;
  return (
    <>
      <div
        style={{
          borderTop: "1px solid var(--color-blue-1)",
          marginTop: "var(--padding-2)",
        }}
      />
      <div className="responsive-card-grid-small" style={{ width: "100%" }}>
        {displayMetrics.map((stat, index) =>
          renderMetric ? (
            renderMetric(stat)
          ) : (
            <MetricDisplay
              key={`simple-row-${index}-${stat.metric}`}
              variant="default"
              label={stripMarkdown(stat.metric)}
              value={Number(stat.current_value).toFixed(2)}
              unit={stat.unit}
              percent={stat.percent_change}
              changeType={getChangeDirection(stat.percent_change)}
            />
          )
        )}
      </div>
      <div
        style={{
          borderBottom: "1px solid var(--color-blue-1)",
          marginTop: "var(--padding-2)",
        }}
      />
    </>
  );
};

// Soft Row Variant: Metrics in a row, soft background, no card border
const SoftRowVariant = ({
  metrics = [],
  maxItems,
  renderMetric,
}: KeyMetricsGridProps) => {
  const displayMetrics = maxItems ? metrics.slice(0, maxItems) : metrics;
  return (
    <div
      className="responsive-card-grid-small"
      style={{
        width: "100%",
        background: "var(--color-background-tertiary)",
        borderRadius: "var(--border-radius-2)",
        padding: "var(--padding-3)",
      }}
    >
      {displayMetrics.map((stat, index) =>
        renderMetric ? (
          renderMetric(stat)
        ) : (
          <div key={index} style={{ textAlign: "center" }}>
            <MetricDisplay
              variant="default"
              label={stripMarkdown(stat.metric)}
              value={Number(stat.current_value).toFixed(2)}
              unit={stat.unit}
              percent={stat.percent_change}
              changeType={getChangeDirection(stat.percent_change)}
            />
          </div>
        )
      )}
    </div>
  );
};

const HorizontalCompactVariant = ({
  metrics = [],
  maxItems,
  renderMetric,
}: KeyMetricsGridProps) => {
  const displayMetrics = maxItems ? metrics.slice(0, maxItems) : metrics;
  return (
    <div className="responsive-card-grid-small">
      {displayMetrics.map((stat, index) =>
        renderMetric ? (
          renderMetric(stat)
        ) : (
          <MetricDisplay
            key={`horizontal-compact-${index}-${stat.metric}`}
            variant="horizontal-compact"
            label={stripMarkdown(stat.metric)}
            value={Number(stat.current_value).toFixed(2)}
            unit={stat.unit}
            percent={stat.percent_change}
            changeType={getChangeDirection(stat.percent_change)}
          />
        )
      )}
    </div>
  );
};

export const KeyMetricsGrid = ({
  metrics,
  maxItems,
  variant = "default",
  renderMetric,
}: KeyMetricsGridProps) => {
  switch (variant) {
    case "modern": {
      return (
        <ModernVariant
          metrics={metrics}
          maxItems={maxItems}
          renderMetric={renderMetric}
        />
      );
    }
    case "simpleCard": {
      return (
        <CardVariant
          metrics={metrics}
          maxItems={maxItems}
          renderMetric={renderMetric}
        />
      );
    }
    case "softCard": {
      return (
        <SoftCardVariant
          metrics={metrics}
          maxItems={maxItems}
          renderMetric={renderMetric}
        />
      );
    }
    case "simpleRow": {
      return (
        <SimpleRowVariant
          metrics={metrics}
          maxItems={maxItems}
          renderMetric={renderMetric}
        />
      );
    }
    case "softRow": {
      return (
        <SoftRowVariant
          metrics={metrics}
          maxItems={maxItems}
          renderMetric={renderMetric}
        />
      );
    }
    case "horizontal-compact": {
      return (
        <HorizontalCompactVariant
          metrics={metrics}
          maxItems={maxItems}
          renderMetric={renderMetric}
        />
      );
    }
    default: {
      return (
        <InlineVariant
          metrics={metrics}
          maxItems={maxItems}
          renderMetric={renderMetric}
        />
      );
    }
  }
};
